// P0 FASE E · Usage widget per il V12 user header.
// -----------------------------------------------------------------------------
// Dropdown "Usage" che mostra 5 metriche con progress bar. Chiamata GET
// /api/user/usage. Soft-limit (>=80%) in warning · hard-limit (>=100%) in
// errore con CTA Upgrade → /pricing.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "@/lib/api";
import { Gauge, AlertTriangle, ArrowRight } from "lucide-react";


const METRIC_LABELS = {
    lead_discovery_count:     "Lead discovery",
    ai_rag_queries:           "AI RAG queries",
    ai_content_generations:   "AI content gen",
    outreach_emails:          "Outreach emails",
    ai_agent_runs:            "AI agent runs",
};


function Progress({ percent }) {
    const color = percent >= 100 ? "bg-[#EF4444]"
                : percent >= 80 ? "bg-[#F59E0B]"
                : "bg-white";
    return (
        <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-[#1A1A1A]">
            <div
                className={`absolute inset-y-0 left-0 ${color}`}
                style={{ width: `${Math.min(100, percent)}%` }}
            />
        </div>
    );
}


export default function UsageWidget() {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const ref = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await apiFetch("/user/usage");
            setData(r);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open && !data) load();
    }, [open, data, load]);

    // Click outside to close.
    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    // Any metric blocked? Show warning dot.
    const anyBlocked = (data?.metrics || []).some(m => m.percent >= 80);

    return (
        <div className="relative" ref={ref} data-testid="usage-widget-root">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                data-testid="usage-widget-toggle"
                className="relative inline-flex items-center gap-1.5 rounded-full border border-[#1A1A1A] bg-[#060606] px-3 py-1.5 text-xs text-white/70 hover:border-white/40 hover:text-white transition-colors"
            >
                <Gauge className="h-3.5 w-3.5" />
                Usage
                {anyBlocked && <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-[#EF4444]" />}
            </button>
            {open && (
                <div
                    data-testid="usage-widget-dropdown"
                    className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-lg border border-[#1A1A1A] bg-black shadow-2xl"
                >
                    <div className="flex items-center justify-between border-b border-[#1A1A1A] px-4 py-3">
                        <span className="text-xs font-medium uppercase tracking-widest text-white/40">Usage · {data?.period || "–"}</span>
                        <span className="rounded-full border border-[#2A2A2A] px-2 py-0.5 text-[10px] text-white/70" data-testid="usage-widget-tier">
                            {data?.tier || "..."}
                        </span>
                    </div>
                    <ul className="divide-y divide-[#1A1A1A]">
                        {loading && !data && (
                            <li className="px-4 py-6 text-center text-xs text-white/40">Loading…</li>
                        )}
                        {(data?.metrics || []).map(m => (
                            <li key={m.metric} className="px-4 py-3" data-testid={`usage-metric-${m.metric}`}>
                                <div className="mb-1.5 flex items-baseline justify-between gap-3">
                                    <span className="text-xs text-white/70">{METRIC_LABELS[m.metric] || m.metric}</span>
                                    <span className="font-mono text-[11px] text-white/50">
                                        <span className={m.percent >= 100 ? "text-[#EF4444]" : m.percent >= 80 ? "text-[#F59E0B]" : "text-white/70"}>
                                            {m.used.toLocaleString("it-IT")}
                                        </span>
                                        {" / "}
                                        {m.limit >= 999999 ? "∞" : m.limit.toLocaleString("it-IT")}
                                    </span>
                                </div>
                                <Progress percent={m.percent} />
                                {m.percent >= 100 && (
                                    <p className="mt-2 flex items-center gap-1 text-[10px] text-[#EF4444]" data-testid={`usage-metric-${m.metric}-blocked`}>
                                        <AlertTriangle className="h-3 w-3" /> Limite raggiunto
                                    </p>
                                )}
                            </li>
                        ))}
                    </ul>
                    <a
                        href="/pricing"
                        data-testid="usage-widget-upgrade-cta"
                        className="flex items-center justify-center gap-1 border-t border-[#1A1A1A] bg-[#060606] px-4 py-3 text-xs text-white hover:bg-[#0A0A0A]"
                    >
                        Upgrade il piano <ArrowRight className="h-3 w-3" />
                    </a>
                </div>
            )}
        </div>
    );
}
